import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError, SearchParams } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  RecruitingStatusCreateSchema,
  RecruitingStatusQueryAllSchema,
  RecruitingStatusQuerySchema,
  RecruitingStatusUpdateSchema,
} from './schemas';

export function useGetRecruitingStatusesControlled({ per_page }: SearchParams<{ per_page: number }>, enabled = true) {
  return useQuery({
    queryKey: ['RECRUITING_STATUSES', per_page],
    enabled,
    queryFn: async () => {
      const res = await apiClient.get<RecruitingStatusQueryAllSchema>({
        endpoint: '/api/v1/recruiting-statuses',
        params: { per_page },
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['RECRUITING_STATUSES'] });
};

export const useGetRecruitingStatusesMutation: UseControlledMutation<RecruitingStatusQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['RECRUITING_STATUSES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<RecruitingStatusQueryAllSchema>({
            endpoint: '/api/v1/recruiting-statuses',
            params: { search, per_page },
          });
          return res?.data || [];
        },
      });
    },
  });

export function useGetRecruitingStatuses(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['RECRUITING_STATUSES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<RecruitingStatusQueryAllSchema>({
        endpoint: '/api/v1/recruiting-statuses',
        params: apiParamStr,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, RecruitingStatusCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/recruiting-statuses/',
        payload,
      }),
  });

export function useGetRecruitingStatus(id: string) {
  return useSuspenseQuery({
    queryKey: ['RECRUITING_STATUS', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: RecruitingStatusQuerySchema }>({
        endpoint: `/api/v1/recruiting-statuses/${id}`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: RecruitingStatusQuerySchema }, ApiError, Partial<RecruitingStatusUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/recruiting-statuses/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['RECRUITING_STATUS_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/recruiting-statuses/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
